import { useEffect } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

import {
  authorNameOrFallback,
  parseChallengePostMedia,
  parseChallengeReactionSummary,
  parseMyReactions,
  type ChallengeFeedComment,
  type ChallengeFeedPost,
  type ChallengePostMedia,
  type ChallengePostReactor,
} from "@fitness-app/data-models/entities/ChallengeFeed";

import { supabase, useAppSelector } from "~/store/initializeStore";

const FEED_PAGE_SIZE = 20;

export type ChallengeFeedSort = "latest" | "oldest" | "top";

export const feedQueryKey = (challengeId: string, sort: ChallengeFeedSort) => ["challenge-feed", challengeId, sort];
export const commentsQueryKey = (postId: string | undefined) => ["challenge-post-comments", postId];

const toFeedPost = (row: {
  id: string;
  createdAt: string;
  updatedAt: string;
  authorId: string;
  authorName: string | null;
  authorAvatar: string | null;
  authorIsTrainer: boolean;
  title: string | null;
  content: string;
  media: unknown;
  pinned: boolean;
  commentCount: number;
  reactions: unknown;
  myReactions: unknown;
  isMine: boolean;
}): ChallengeFeedPost => ({
  id: row.id,
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
  authorId: row.authorId,
  authorName: authorNameOrFallback(row.authorName),
  authorAvatar: row.authorAvatar,
  authorIsTrainer: row.authorIsTrainer,
  title: row.title,
  content: row.content,
  media: parseChallengePostMedia(row.media),
  pinned: row.pinned,
  commentCount: row.commentCount,
  reactions: parseChallengeReactionSummary(row.reactions),
  myReactions: parseMyReactions(row.myReactions),
  isMine: row.isMine,
});

/**
 * Live board updates — the trainer has the page open while participants comment and react, so the
 * counters have to move on their own. Payloads are ignored: a change only invalidates the caches,
 * which re-read through the RPCs that carry the author display names.
 */
const useChallengeFeedRealtime = (challengeId: string, enabled: boolean) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const filter = `challengeId=eq.${challengeId}`;
    const channel = supabase
      .channel(`admin-challenge-feed:${challengeId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "challenge_post", filter }, () => {
        void queryClient.invalidateQueries({ queryKey: ["challenge-feed", challengeId] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "challenge_post_comment", filter }, (payload) => {
        const postId =
          (payload.new as { postId?: string } | null)?.postId ?? (payload.old as { postId?: string } | null)?.postId;
        void queryClient.invalidateQueries({ queryKey: ["challenge-feed", challengeId] });
        if (postId) {
          void queryClient.invalidateQueries({ queryKey: commentsQueryKey(postId) });
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "challenge_post_reaction", filter }, () => {
        void queryClient.invalidateQueries({ queryKey: ["challenge-feed", challengeId] });
      })
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [challengeId, enabled, queryClient]);
};

export const useChallengeFeed = (challengeId: string, sort: ChallengeFeedSort, enabled: boolean) => {
  const query = useInfiniteQuery({
    queryKey: feedQueryKey(challengeId, sort),
    enabled,
    queryFn: async ({ pageParam = 0 }): Promise<{ posts: ChallengeFeedPost[]; nextOffset: number }> => {
      const { data, error } = await supabase.rpc("challenge_feed", {
        p_challenge: challengeId,
        p_limit: FEED_PAGE_SIZE,
        p_offset: pageParam,
        p_sort: sort,
      });
      if (error) {
        throw new Error(error.message);
      }
      const posts = (data ?? []).map(toFeedPost);
      return { posts, nextOffset: pageParam + posts.length };
    },
    getNextPageParam: (lastPage) => (lastPage.posts.length < FEED_PAGE_SIZE ? undefined : lastPage.nextOffset),
  });

  useChallengeFeedRealtime(challengeId, enabled);

  return {
    posts: query.data?.pages.flatMap((page) => page.posts) ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  };
};

export const useChallengePostComments = (postId: string | undefined) =>
  useQuery({
    queryKey: commentsQueryKey(postId),
    enabled: Boolean(postId),
    queryFn: async (): Promise<ChallengeFeedComment[]> => {
      const { data, error } = await supabase.rpc("challenge_post_comments", { p_post: postId ?? "" });
      if (error) {
        throw new Error(error.message);
      }
      return (data ?? []).map((row) => ({
        ...row,
        authorName: authorNameOrFallback(row.authorName),
        media: parseChallengePostMedia(row.media),
      }));
    },
  });

export const useChallengePostReactors = (postId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ["challenge-post-reactors", postId],
    enabled: Boolean(postId) && enabled,
    queryFn: async (): Promise<ChallengePostReactor[]> => {
      const { data, error } = await supabase.rpc("challenge_post_reactors", { p_post: postId ?? "" });
      if (error) {
        throw new Error(error.message);
      }
      return (data ?? []).map((row) => ({
        userId: row.userId,
        emoji: row.emoji,
        displayName: authorNameOrFallback(row.displayName),
        avatarUrl: row.avatarUrl,
      }));
    },
  });

/** Every board write in one hook — they all invalidate the same feed, and the page needs them together. */
export const useChallengeFeedActions = (challengeId: string) => {
  const queryClient = useQueryClient();
  const userId = useAppSelector((store) => store.user.data?.id);

  const invalidateFeed = () => {
    void queryClient.invalidateQueries({ queryKey: ["challenge-feed", challengeId] });
  };

  const createPost = useMutation({
    mutationFn: async ({
      title,
      content,
      media,
    }: {
      title: string | null;
      content: string;
      media: ChallengePostMedia[];
    }) => {
      if (!userId) {
        throw new Error("Brak zalogowanego użytkownika");
      }
      const trimmed = content.trim();
      if (!trimmed && !media.length) {
        throw new Error("Post musi mieć treść albo załącznik");
      }
      const { error } = await supabase.from("challenge_post").insert({
        challengeId,
        authorId: userId,
        title,
        content: trimmed,
        media: media as never,
      } as never);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Post opublikowany");
      invalidateFeed();
    },
    onError: (error: Error) => void message.error(`Nie udało się opublikować: ${error.message}`),
  });

  const updatePost = useMutation({
    mutationFn: async ({
      id,
      title,
      content,
      media,
    }: {
      id: string;
      title: string | null;
      content: string;
      media: ChallengePostMedia[];
    }) => {
      const trimmed = content.trim();
      if (!trimmed && !media.length) {
        throw new Error("Post musi mieć treść albo załącznik");
      }
      const { error } = await supabase
        .from("challenge_post")
        .update({ title, content: trimmed, media: media as never } as never)
        .eq("id", id);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Post zaktualizowany");
      invalidateFeed();
    },
    onError: (error: Error) => void message.error(`Nie udało się zapisać: ${error.message}`),
  });

  const setPinned = useMutation({
    mutationFn: async ({ id, pinned }: { id: string; pinned: boolean }) => {
      const { error } = await supabase
        .from("challenge_post")
        .update({ pinned } as never)
        .eq("id", id);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: (_data, { pinned }) => {
      void message.success(pinned ? "Post przypięty" : "Post odpięty");
      invalidateFeed();
    },
    onError: (error: Error) => void message.error(`Nie udało się zmienić przypięcia: ${error.message}`),
  });

  const deletePost = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("challenge_post").delete().eq("id", id);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      void message.success("Post usunięty");
      invalidateFeed();
    },
    onError: (error: Error) => void message.error(`Nie udało się usunąć: ${error.message}`),
  });

  const toggleReaction = useMutation({
    mutationFn: async ({ postId, emoji }: { postId: string; emoji: string }) => {
      const { error } = await supabase.rpc("toggle_challenge_post_reaction", { p_post: postId, p_emoji: emoji });
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: invalidateFeed,
    onError: (error: Error) => void message.error(`Nie udało się zapisać reakcji: ${error.message}`),
  });

  const addComment = useMutation({
    mutationFn: async ({
      postId,
      content,
      media = [],
    }: {
      postId: string;
      content: string;
      media?: ChallengePostMedia[];
    }) => {
      if (!userId) {
        throw new Error("Brak zalogowanego użytkownika");
      }
      const trimmed = content.trim();
      if (!trimmed && !media.length) {
        throw new Error("Komentarz nie może być pusty");
      }
      const { error } = await supabase.from("challenge_post_comment").insert({
        postId,
        challengeId,
        authorId: userId,
        content: trimmed,
        media,
      } as never);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: (_data, _error, { postId }) => {
      void queryClient.invalidateQueries({ queryKey: commentsQueryKey(postId) });
      invalidateFeed();
    },
    onError: (error: Error) => void message.error(`Nie udało się dodać komentarza: ${error.message}`),
  });

  const deleteComment = useMutation({
    mutationFn: async ({ commentId }: { commentId: string; postId: string }) => {
      const { error } = await supabase.from("challenge_post_comment").delete().eq("id", commentId);
      if (error) {
        throw new Error(error.message);
      }
    },
    onSettled: (_data, _error, { postId }) => {
      void queryClient.invalidateQueries({ queryKey: commentsQueryKey(postId) });
      invalidateFeed();
    },
    onError: (error: Error) => void message.error(`Nie udało się usunąć komentarza: ${error.message}`),
  });

  return { createPost, updatePost, setPinned, deletePost, toggleReaction, addComment, deleteComment };
};
